import { useCallback, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { TextField, InputAdornment, TablePagination, Typography, Button } from "@mui/material";
import { confirm } from "material-ui-confirm";
import { Box, CircularProgress, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, IconButton, } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from '@mui/icons-material/Add'
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import useDebounce from "../../../lib/useDebounce";
import axiosClient from "../../../lib/axios";

export default function SessaoConfiguracao<T extends { id?: number | string }>({
  endpoints,
  colunas,
  queryKey,
  queryParams = {},
  onEditar,
  onAdicionar = () => { },
  mensagemSemDados = "Nenhum dado encontrado.",
  mensagemSucessoDeletar = "Item excluído com sucesso!",
  mensagemErroDeletar = "Erro ao excluir item!",
  mensagemParaDeletar = "Deseja realmente deletar este item?",
  possuiAdicionar = true,
  podeEditar = () => true,
  podeDeletar = () => true,
  titulo,
  botoesPersonalizados = [],
}: SessaoConfiguracaoProps<T>) {
  const queryClient = useQueryClient();
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [filtros, setFiltros] = useState({
    pagina: 0,
    porPagina: 6,
  });

  const termoPesquisaDebounced = useDebounce(termoPesquisa) as string;

  const { data, isLoading } = useQuery({
    queryKey: [queryKey, filtros, termoPesquisaDebounced],
    queryFn: () =>
      axiosClient.get(endpoints.listar, {
        params: {
          limit: filtros.porPagina,
          page: filtros.pagina + 1,
          pesquisar: termoPesquisaDebounced,
          ...queryParams,
        },
      }),
    retry: false,
  });

  const { mutate: deletarItem, isLoading: isLoadingDelete } = useMutation({
    mutationFn: (id: number | string) =>
      axiosClient.delete(`${endpoints.deletar}/${id}`),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar(mensagemErroDeletar, { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
      enqueueSnackbar(mensagemSucessoDeletar, { variant: "success" });
    },
  });

  const handleChangePage = useCallback(
    (_: React.MouseEvent<HTMLButtonElement> | null, novaPag: number) => {
      setFiltros((prev) => ({ ...prev, pagina: novaPag }));
    },
    []
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFiltros({
        porPagina: parseInt(event.target.value, 10),
        pagina: 0,
      });
    },
    []
  );

  const handleDelete = useCallback(
    async (id: number | string) => {
      confirm({ description: mensagemParaDeletar })
        .then(() => deletarItem(id))
        .catch(() => { });
    },
    [deletarItem, mensagemParaDeletar]
  );

  const dados = data?.data.data || [];
  const total = data?.data.total || 0;

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} px={3} pt={2}>
        <Typography variant="h6">{titulo}</Typography>

        {
          possuiAdicionar &&
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => onAdicionar()}>
            Adicionar
          </Button>
        }

      </Box>
      <TextField
        label="Pesquisar"
        variant="outlined"
        fullWidth
        value={termoPesquisa}
        onChange={(e) => {
          setTermoPesquisa(e.target.value);
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }
        }}
      />

      {isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="tabela configuração">
              <TableHead>
                <TableRow>
                  {colunas.map((coluna, index) => (
                    <TableCell key={index} align="left">
                      <b>{coluna.titulo}</b>
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={colunas.length + 1} align="center">
                      {mensagemSemDados}
                    </TableCell>
                  </TableRow>
                )}
                {dados.map((item: T, index: number) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "& td, & th": { height: "73px" }
                    }}
                  >
                    {colunas.map((coluna, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align="left"
                        component={colIndex === 0 ? "th" : "td"}
                        scope={colIndex === 0 ? "row" : undefined}
                      >
                        {coluna.render
                          ? coluna.render(item[coluna.campo], item)
                          : item[coluna.campo] == null ? '-' : String(item[coluna.campo])}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        {podeEditar(item) &&
                          <IconButton
                            aria-label="editar"
                            onClick={() => onEditar(item)}
                            title="Editar"
                          >
                            <EditIcon />
                          </IconButton>
                        }

                        {botoesPersonalizados.map((botao, index) => (
                          <IconButton
                            key={`custom-${index}`}
                            aria-label={`botao-personalizado-${index}`}
                            onClick={() => botao.onClick(item)}
                            disabled={botao.disabled || false}
                            title={botao.tooltip}
                          >
                            {botao.icon}
                          </IconButton>
                        ))}

                        {isLoadingDelete && <CircularProgress size={18} />}
                        {!isLoadingDelete && item.id && podeDeletar(item) && (
                          <IconButton
                            aria-label="deletar"
                            onClick={() => handleDelete(item.id!)}
                            title="Deletar"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={filtros.pagina}
            onPageChange={handleChangePage}
            rowsPerPage={filtros.porPagina}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página"
          />
        </>
      )}
    </>
  );
}



interface ConfiguracaoEndpoints {
  listar: string;
  deletar: string;
}

export interface ConfiguracaoColunas<T> {
  campo: keyof T;
  titulo: string;
  render?: (value: any, item: T) => React.ReactNode;
}

interface BotaoPersonalizado<T> {
  icon: React.ReactElement;
  onClick: (item: T) => void;
  disabled?: boolean;
  tooltip: string;
}

interface SessaoConfiguracaoProps<T> {
  titulo: string;
  endpoints: ConfiguracaoEndpoints;
  colunas: ConfiguracaoColunas<T>[];
  queryKey: string;
  queryParams?: Record<string, any>;
  onEditar: (item: T) => void;
  onAdicionar?: () => void;
  mensagemSemDados?: string;
  mensagemSucessoDeletar?: string;
  mensagemErroDeletar?: string;
  possuiAdicionar?: boolean;
  mensagemParaDeletar?: string;
  podeEditar?: (item: T) => boolean;
  podeDeletar?: (item: T) => boolean;
  botoesPersonalizados?: BotaoPersonalizado<T>[];
}